import type { IdentityContext, OrganizationMembership, OrganizationRepository } from '@zetyar/data-contracts'
import type { Membership, Organization, RoleName } from '@zetyar/domain'
import { isKnownRole } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface MembershipRow {
  readonly id: string
  readonly organization_id: string
  readonly person_id: string
  readonly role: string
  readonly status: string
  readonly joined_at: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly organization: {
    readonly id: string
    readonly name: string
    readonly slug: string
    readonly logo_url: string | null
    readonly logo_dark_url: string | null
    readonly favicon_url: string | null
    readonly brand_color: string | null
    readonly primary_domain: string | null
    readonly features: Record<string, boolean> | null
    readonly status: string
    readonly timezone: string
    readonly locale: string
    readonly created_by: string
    readonly created_at: string
    readonly updated_at: string
    readonly deleted_at: string | null
  }
  readonly membership_roles: readonly { readonly role: string }[]
}

function mapOrganizationMembership(row: MembershipRow): OrganizationMembership {
  const organization: Organization = {
    id: row.organization.id,
    name: row.organization.name,
    slug: row.organization.slug,
    description: null,
    logoUrl: row.organization.logo_url,
    logoDarkUrl: row.organization.logo_dark_url,
    faviconUrl: row.organization.favicon_url,
    brandColor: row.organization.brand_color,
    primaryDomain: row.organization.primary_domain,
    features: row.organization.features ?? {},
    status: row.organization.status as Organization['status'],
    timezone: row.organization.timezone,
    locale: row.organization.locale,
    createdBy: row.organization.created_by,
    createdAt: row.organization.created_at,
    updatedAt: row.organization.updated_at,
    deletedAt: row.organization.deleted_at,
  }

  const roles: RoleName[] = row.membership_roles.map((r) => r.role).filter(isKnownRole)
  if (roles.length === 0 && isKnownRole(row.role)) roles.push(row.role)

  const membership: Membership = {
    id: row.id,
    organizationId: row.organization_id,
    userId: row.person_id,
    roles,
    status: row.status as Membership['status'],
    joinedAt: row.joined_at ?? row.created_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    organization,
  }

  return { organization, membership }
}

export function createSupabaseOrganizationRepository(client: SupabaseClient): OrganizationRepository {
  return {
    async listForPerson(context: IdentityContext) {
      const { data, error } = await client
        .from('memberships')
        .select(`
          id, organization_id, person_id, role, status, joined_at, created_at, updated_at,
          organization:organizations (
            id, name, slug, logo_url, logo_dark_url, favicon_url, brand_color, primary_domain, features,
            status, timezone, locale, created_by, created_at, updated_at, deleted_at
          ),
          membership_roles (role)
        `)
        .eq('person_id', context.personId)

      if (error) throw error
      return (data as unknown as MembershipRow[]).map(mapOrganizationMembership)
    },
  }
}

// Each organization administers its own modules: features travels in the same UPDATE as the rest of
// the settings, so a rejection surfaces as one visible error instead of failing silently. RLS still
// limits the write to organization_admin or the platform (0003_rls_identity.sql).
export async function updateOrganizationSettings(
  client: SupabaseClient,
  organizationId: string,
  input: {
    name: string
    timezone: string
    locale: string
    logoUrl?: string | null
    features?: Record<string, boolean>
  },
): Promise<void> {
  const update: Record<string, unknown> = { name: input.name, timezone: input.timezone, locale: input.locale }
  if (input.logoUrl !== undefined) update.logo_url = input.logoUrl
  if (input.features !== undefined) update.features = input.features

  const { error } = await client.from('organizations').update(update).eq('id', organizationId)
  if (error) throw error
}
